import React from 'react';
import { ModalNextProvider } from '@jutro/components';
import ModalCtrl from 'sg-modal-ctrl';
import {
    SgSessionStorageService,
    SgProductService,
    SgGdprUtilService,
    SgSubmissionUtil,
    ADOBE_TOPICS,
    COMMON_TAGGING_PROPERTIES
} from 'saga-modules';
import { SgCustomerPreferenceService } from 'sg-capability-gdpr';
import CommonConstants from 'sg-common-constants';
import { events } from 'gw-platform-events-js';
import _ from 'lodash';
import { publish } from '@jutro/events';

// A service to manage the gdpr related functions

let cpcPermissionId;
let cpcQuestionId;
const gdprDivisionsNames = {
    insurance: 'Insurance',
    travel: 'Holidays and Cruises',
    finance: 'Personal Finance',
    magazine: 'Saga Magazine'
};
const PRODUCT_ID = {
    SMS: 'SMS',
    HOME: 'Home',
    PRIVATE_MEDICAL: 'PI',
    TRAVEL: 'TR'
};

function setSessionStorageItem(key, val) {
    let newValue = val;
    if (typeof newValue !== 'string') {
        newValue = JSON.stringify(val);
    }
    return SgSessionStorageService.setItem(SgSessionStorageService.KEY[key], newValue);
}

function getSessionStorageItem(key) {
    const val = SgSessionStorageService.getItem(SgSessionStorageService.KEY[key]);
    if (val !== null && typeof val === 'string') {
        return JSON.parse(val);
    }
    return val;
}

function showCommunicationPreferences() {
    return getSessionStorageItem('GDPR_COMMSPREF');
}

// ** Returns policyholder from submisson */
const getPolicyHolderVal = (modelVM) => {
    return SgSubmissionUtil.getAccountHolder(modelVM).value;
};

// ** Returns address from submisson */
const getAddressVal = (modelVM) => {
    return modelVM.baseData.policyAddress.value;
};

const cpcCalledForThisCustomer = () => {
    const cpcCalled = getSessionStorageItem('GDPR_CPCCALLED');
    return cpcCalled === undefined ? false : cpcCalled;
};

// ** Returns policyPurchased boolean */
const policyPurchased = (modelVM) => {
    return modelVM.baseData.policyPeriodStatus_Ext.value === CommonConstants.POLICY_PERIOD_STATUS.BOUND;
};

const getProductID = (modelVM) => {
    const productCode = modelVM.baseData.productCode.value;
    switch (productCode) {
        case CommonConstants.MOTOR_PRODUCT_CODE:
            return PRODUCT_ID.SMS;
        case CommonConstants.HOME_PRODUCT_CODE:
            return PRODUCT_ID.HOME;
        case CommonConstants.PRIVATE_MEDICAL_INSURANCE_CODE:
            return PRODUCT_ID.PRIVATE_MEDICAL;
        case CommonConstants.TRAVEL_PRODUCT_CODE:
            return PRODUCT_ID.TRAVEL;
        default:
            return productCode;
    }
};

function toSafeString(value) {
    // check for undefined as calling toString on undefined will throw exception
    if (value === undefined) return undefined;

    return value.toString();
}

function safeNumberToString(value) {
    if (value === undefined) return undefined;

    const result = value.toString();
    return result.length > 1 && result.startsWith('0')
        ? result.substring(1)
        : result;
}

function isValid(value) {
    return !!value;
}

function insuranceNotSelectedForEmailOrPost(data) {
    if (data) {
        const insuranceDivision = data.find((d) => d.name === gdprDivisionsNames.insurance) || {};
        return !insuranceDivision.emailSelected || !insuranceDivision.letterSelected;
    }
    return undefined;
}

// ** create object to store reqdata  */
function buildReqData(modelVM) {
    const address = getAddressVal(modelVM);
    const policyHolder = getPolicyHolderVal(modelVM);
    const buildData = {
        firstName: policyHolder.firstName,
        surname: policyHolder.lastName,
        birthDate: {
            day: policyHolder.dateOfBirth.day,
            month: policyHolder.dateOfBirth.month,
            monthViewValue: policyHolder.dateOfBirth.monthViewValue,
            year: policyHolder.dateOfBirth.year
        },
        houseName: address.addressLine1 === undefined ? '' : address.addressLine1,
        houseNumber: address.addressLine2,
        street: address.addressLine3,
        town: address.city,
        county: address.county,
        postcode: address.postalCode,
        isAggJourney: SgSubmissionUtil.isAggregatorQuote(modelVM),
        cpcCalledForThisCustomer: cpcCalledForThisCustomer(),
        productId: getProductID(modelVM),
        permissionCategoryAll: true
    };
    return buildData;
}

// ** build request to save   */
function dataToSave(modelVM) {
    const gdprData = getSessionStorageItem('GDPR_DATA') || buildReqData(modelVM);
    setSessionStorageItem('GDPR_DATA', gdprData);
    const preferences = SgSubmissionUtil.getAccountHolder(modelVM).communicationPreferences.value;
    cpcQuestionId = '01';
    if (preferences.showMultipleChannels) {
        cpcQuestionId = '01';
    }
    if (preferences.scenariotwo) {
        cpcQuestionId = '02';
    }
    if (preferences.scenariotthree || preferences.showSingleChannelSingleDivision) {
        cpcQuestionId = '03';
    }
    if (preferences.showSingleDivisionMultipleChannels) {
        cpcQuestionId = '04';
    }
    const buildData = {
        emailSelectedAcrossSelectedDivisions: (preferences.isGdprReConsent && preferences.scenariotwo)
        || preferences.showSingleDivisionMultipleChannels ? preferences.insuranceEmailSelected && preferences.travelEmailSelected
            && preferences.financialEmailSelected : preferences.hasEmailSelected,
        postSelectedAcrossSelectedDivisions: (preferences.isGdprReConsent && preferences.scenariotwo)
        || preferences.showSingleDivisionMultipleChannels ? preferences.insuranceLetterSelected && preferences.travelLetterSelected
            && preferences.financialLetterSelected : preferences.hasLetterSelected,
        phoneSelectedAcrossSelectedDivisions: (preferences.isGdprReConsent && preferences.scenariotwo)
        || preferences.showSingleDivisionMultipleChannels ? preferences.insurancePhoneSelected && preferences.travelPhoneSelected
            && preferences.financialPhoneSelected : preferences.hasPhoneSelected,
        gdprDivision: [{
            name: gdprDivisionsNames.insurance,
            checked: (preferences.isGdprReConsent && preferences.scenariotwo)
            || preferences.showSingleDivisionMultipleChannels ? preferences.insuranceEmailSelected || preferences.insuranceLetterSelected
                || preferences.insurancePhoneSelected || preferences.hasInsuranceSelected : preferences.hasInsuranceSelected,
            emailSelected: preferences.insuranceEmailSelected,
            letterSelected: preferences.insuranceLetterSelected,
            channelPhoneNoFlag: preferences.insurancePhoneSelected
        },
        {
            name: gdprDivisionsNames.travel,
            checked: (preferences.isGdprReConsent && preferences.scenariotwo) || preferences.showSingleDivisionMultipleChannels
                ? preferences.travelEmailSelected || preferences.travelLetterSelected || preferences.travelPhoneSelected
                || preferences.hasTravelSelected : preferences.hasTravelSelected,
            emailSelected: preferences.travelEmailSelected,
            letterSelected: preferences.travelLetterSelected,
            channelPhoneNoFlag: preferences.travelPhoneSelected
        },
        {
            name: gdprDivisionsNames.finance,
            checked: (preferences.isGdprReConsent && preferences.scenariotwo) || preferences.showSingleDivisionMultipleChannels
                ? preferences.financialEmailSelected || preferences.financialLetterSelected || preferences.financialPhoneSelected
                || preferences.hasFinancialSelected : preferences.hasFinancialSelected,
            emailSelected: preferences.financialEmailSelected,
            letterSelected: preferences.financialLetterSelected,
            channelPhoneNoFlag: preferences.financialPhoneSelected
        }],
        productId: gdprData.productId,
        questionId: cpcQuestionId === undefined ? gdprData.cpcQuestionId : cpcQuestionId,
        permissionId: cpcPermissionId === undefined ? gdprData.cpcPermissionId : cpcPermissionId,
        policyPurchased: policyPurchased(modelVM),
        showGDPR: (showCommunicationPreferences() === undefined || showCommunicationPreferences() === null)
            ? gdprData.showGDPR : showCommunicationPreferences()
    };
    buildData.gdprDivision.push({
        name: gdprDivisionsNames.magazine,
        checked: (preferences.isGdprReConsent && preferences.scenariotwo) || preferences.showSingleDivisionMultipleChannels
            ? preferences.magazineEmailSelected || preferences.magazineLetterSelected || preferences.magazinePhoneSelected
            || preferences.hasMagazineSelected : preferences.hasMagazineSelected,
        emailSelected: preferences.magazineEmailSelected,
        letterSelected: preferences.magazineLetterSelected,
        channelPhoneNoFlag: preferences.magazinePhoneSelected
    });
    return buildData;
}

// ** get session data  */
const getGdprSessionObject = () => {
    return getSessionStorageItem('GDPR_DATA');
};

// ** Saves the data in session
// * @param data the data to store in the session
//  * @param newGdprData if true, creates a new storage with that data, otherwise it extends the existing data

const saveDataInSessionKey = (data, newGdprData) => {
    if (newGdprData) {
        setSessionStorageItem('GDPR_DATA', data);
    } else {
        const gdprData = getGdprSessionObject() || {};
        const mergeData = Object.assign(gdprData, data);
        setSessionStorageItem('GDPR_DATA', mergeData);
    }
};

// * Saves the gdprData in the session
// * @param gdprData
// * @param newGdprData if true, creates a new storage with that data, otherwise it extends the existing data

const saveGdprResponseInSessionKey = (gdprData, newGdprData) => {
    let hasInsuranceSelected;
    let hasTravelSelected;
    let hasFinancialSelected;
    let isOptedInToAllAreas; // it will be true if all divisions are checked, false if only some are checked, undefined otherwise.
    // CRF207-2
    let hasMagazineSelected;
    let isOptedOutToAllAreas;

    let insuranceEmailSelected;
    let insuranceLetterSelected;
    let insurancePhoneSelected;

    let travelEmailSelected;
    let travelLetterSelected;
    let travelPhoneSelected;

    let financialEmailSelected;
    let financialLetterSelected;
    let financialPhoneSelected;

    let magazineEmailSelected;
    let magazineLetterSelected;
    let magazinePhoneSelected;

    if (gdprData.gdprDivision) {
        const insuranceDivision = gdprData.gdprDivision.find((d) => d.name === gdprDivisionsNames.insurance) || {};
        hasInsuranceSelected = insuranceDivision.checked;
        insuranceEmailSelected = insuranceDivision.emailSelected;
        insuranceLetterSelected = insuranceDivision.letterSelected;
        insurancePhoneSelected = insuranceDivision.channelPhoneNoFlag;
        const travelDivision = gdprData.gdprDivision.find((d) => d.name === gdprDivisionsNames.travel) || {};
        hasTravelSelected = travelDivision.checked;
        travelEmailSelected = travelDivision.emailSelected;
        travelLetterSelected = travelDivision.letterSelected;
        travelPhoneSelected = travelDivision.channelPhoneNoFlag;
        const financialDivision = gdprData.gdprDivision.find((d) => d.name === gdprDivisionsNames.finance) || {};
        hasFinancialSelected = financialDivision.checked;
        financialEmailSelected = financialDivision.emailSelected;
        financialLetterSelected = financialDivision.letterSelected;
        financialPhoneSelected = financialDivision.channelPhoneNoFlag;

        const magazineDivision = gdprData.gdprDivision.find((d) => d.name === gdprDivisionsNames.magazine) || {};
        hasMagazineSelected = magazineDivision.checked;
        magazineEmailSelected = magazineDivision.emailSelected;
        magazineLetterSelected = magazineDivision.letterSelected;
        magazinePhoneSelected = magazineDivision.channelPhoneNoFlag;

        if (gdprData.gdprDivision.find((d) => d.checked)) {
            isOptedInToAllAreas = gdprData.gdprDivision.every((d) => d.checked);
        } else {
            isOptedOutToAllAreas = gdprData.gdprDivision.every((d) => !d.checked);
        }
    }

    const data = {
        showGDPR: gdprData.showGDPR,
        showFullGdpr: gdprData.showFullGdpr,
        hasEmailSelected: gdprData.emailSelectedAcrossSelectedDivisions,
        hasLetterSelected: gdprData.postSelectedAcrossSelectedDivisions,
        hasPhoneSelected: gdprData.phoneSelectedAcrossSelectedDivisions,
        hasInsuranceSelected: hasInsuranceSelected,
        insuranceEmailSelected: insuranceEmailSelected,
        insuranceLetterSelected: insuranceLetterSelected,
        insurancePhoneSelected: insurancePhoneSelected,
        hasTravelSelected: hasTravelSelected,
        travelEmailSelected: travelEmailSelected,
        travelLetterSelected: travelLetterSelected,
        travelPhoneSelected: travelPhoneSelected,
        hasFinancialSelected: hasFinancialSelected,
        financialEmailSelected: financialEmailSelected,
        financialLetterSelected: financialLetterSelected,
        financialPhoneSelected: financialPhoneSelected,
        isOptedInToAllAreas: isOptedInToAllAreas,
        hasSeenCommunicationPreferences: gdprData.hasSeenCommunicationPreferences,
        cpcPermissionId: gdprData.permissionId,
        cpcQuestionId: gdprData.questionId,
        hasMagazineSelected: hasMagazineSelected,
        magazineEmailSelected: magazineEmailSelected,
        magazineLetterSelected: magazineLetterSelected,
        magazinePhoneSelected: magazinePhoneSelected,
        isOptedOutToAllAreas: isOptedOutToAllAreas,
    };

    saveDataInSessionKey(data, newGdprData);
};

// ** delete gdprData session storage  */
const deleteGdprData = () => {
    const gdprData = getSessionStorageItem('GDPR_DATA');
    if (gdprData) {
        // remove
        SgSessionStorageService.removeItem(SgSessionStorageService.KEY.GDPR_DATA);
    }
};

// ** Compares the reqData in sessionstorage with latest data from model  */
const compareDataWithSessionStorage = (model) => {
    const gdprData = getSessionStorageItem('GDPR_DATA');
    const dataToCompare = buildReqData(model);
    if (gdprData) {
        return toSafeString(gdprData.firstName) === toSafeString(dataToCompare.firstName)
            && toSafeString(gdprData.surname) === toSafeString(dataToCompare.surname)
            && safeNumberToString(parseInt(gdprData.birthDate.day, 10)) === safeNumberToString(parseInt(dataToCompare.birthDate.day, 10))
            && safeNumberToString(parseInt(gdprData.birthDate.monthViewValue, 10))
            === safeNumberToString(parseInt(dataToCompare.birthDate.monthViewValue, 10))
            && safeNumberToString(parseInt(gdprData.birthDate.year, 10)) === safeNumberToString(parseInt(dataToCompare.birthDate.year, 10))
            && toSafeString(gdprData.houseName) === toSafeString(dataToCompare.houseName)
            && toSafeString(gdprData.houseNumber) === toSafeString(dataToCompare.houseNumber)
            && toSafeString(gdprData.street) === toSafeString(dataToCompare.street)
            && toSafeString(gdprData.postcode) === toSafeString(dataToCompare.postcode);
    }
    return false;
};

// ** Saves Gdpr related answers in the Session storage  */
const saveGdprAnswersInSessionStorage = (model) => {
    const gdprData = getGdprSessionObject() || {};
    const preferences = SgSubmissionUtil.getAccountHolder(model).value.communicationPreferences;
    if (preferences !== undefined) {
        gdprData.hasEmailSelected = preferences.hasEmailSelected;
        gdprData.hasLetterSelected = preferences.hasLetterSelected;
        gdprData.hasPhoneSelected = preferences.hasPhoneSelected;
        gdprData.hasInsuranceSelected = preferences.hasInsuranceSelected;
        gdprData.insuranceEmailSelected = preferences.insuranceEmailSelected;
        gdprData.insuranceLetterSelected = preferences.insuranceLetterSelected;
        gdprData.insurancePhoneSelected = preferences.insurancePhoneSelected;
        gdprData.hasTravelSelected = preferences.hasTravelSelected;
        gdprData.travelEmailSelected = preferences.travelEmailSelected;
        gdprData.travelLetterSelected = preferences.travelLetterSelected;
        gdprData.travelPhoneSelected = preferences.travelPhoneSelected;
        gdprData.hasFinancialSelected = preferences.hasFinancialSelected;
        gdprData.financialEmailSelected = preferences.financialEmailSelected;
        gdprData.financialLetterSelected = preferences.financialLetterSelected;
        gdprData.financialPhoneSelected = preferences.financialPhoneSelected;
        gdprData.isOptedInToAllAreas = preferences.isOptedInToAllAreas;
        gdprData.isOptedOutToAllAreas = preferences.isOptedOutToAllAreas;
        gdprData.hasMagazineSelected = preferences.hasMagazineSelected;
        gdprData.magazineEmailSelected = preferences.magazineEmailSelected;
        gdprData.magazineLetterSelected = preferences.magazineLetterSelected;
        gdprData.magazinePhoneSelected = preferences.magazinePhoneSelected;
        gdprData.isGdprReConsent = preferences.isGdprReConsent;
        gdprData.scenariotwo = preferences.scenariotwo;
        gdprData.showSingleDivisionMultipleChannels = preferences.showSingleDivisionMultipleChannels;
    }
    gdprData.showGDPR = showCommunicationPreferences();
    gdprData.cpcCalledForThisCustomer = getSessionStorageItem('GDPR_CPCCALLED');
    gdprData.cpcPermissionId = cpcPermissionId === undefined ? gdprData.cpcPermissionId : cpcPermissionId;
    gdprData.cpcQuestionId = cpcQuestionId === undefined ? gdprData.cpcQuestionId : cpcQuestionId;
    setSessionStorageItem('GDPR_DATA', gdprData);
};

const buildDataToSave = (model) => {
    return Object.assign(dataToSave(model), buildReqData(model));
};

// ** Compares the Gdpr Answers in sessionstorage with latest data from model  */
const compareGdprAnswersWithSessionStorage = (model) => {
    const gdprData = getGdprSessionObject();
    const preferences = SgSubmissionUtil.getAccountHolder(model).communicationPreferences.value;
    if (gdprData && preferences !== undefined) {
        return gdprData.hasEmailSelected === preferences.hasEmailSelected
            && gdprData.hasLetterSelected === preferences.hasLetterSelected
            && gdprData.hasPhoneSelected === preferences.hasPhoneSelected
            && gdprData.hasInsuranceSelected === preferences.hasInsuranceSelected
            && gdprData.insuranceEmailSelected === preferences.insuranceEmailSelected
            && gdprData.insuranceLetterSelected === preferences.insuranceLetterSelected
            && gdprData.insurancePhoneSelected === preferences.insurancePhoneSelected
            && gdprData.hasTravelSelected === preferences.hasTravelSelected
            && gdprData.travelEmailSelected === preferences.travelEmailSelected
            && gdprData.travelLetterSelected === preferences.travelLetterSelected
            && gdprData.travelPhoneSelected === preferences.travelPhoneSelected
            && gdprData.hasFinancialSelected === preferences.hasFinancialSelected
            && gdprData.financialEmailSelected === preferences.financialEmailSelected
            && gdprData.financialLetterSelected === preferences.financialLetterSelected
            && gdprData.financialPhoneSelected === preferences.financialPhoneSelected
            && gdprData.isOptedInToAllAreas === preferences.isOptedInToAllAreas
            && gdprData.isOptedOutToAllAreas === preferences.isOptedOutToAllAreas
            && gdprData.hasMagazineSelected === preferences.hasMagazineSelected
            && gdprData.magazineEmailSelected === preferences.magazineEmailSelected
            && gdprData.magazineLetterSelected === preferences.magazineLetterSelected
            && gdprData.magazinePhoneSelected === preferences.magazinePhoneSelected;
    }
    return preferences === undefined;
};

const resetGdprQuestions = (model) => {
    if (SgSubmissionUtil.getAccountHolder(model).communicationPreferences.value === undefined) {
        SgSubmissionUtil.getAccountHolder(model).communicationPreferences.value = {};
    }
    const preferences = SgSubmissionUtil.getAccountHolder(model).communicationPreferences.value;
    preferences.hasEmailSelected = undefined;
    preferences.hasLetterSelected = undefined;
    preferences.hasPhoneSelected = undefined;
    preferences.hasInsuranceSelected = undefined;
    preferences.insuranceEmailSelected = undefined;
    preferences.insuranceLetterSelected = undefined;
    preferences.insurancePhoneSelected = undefined;
    preferences.hasTravelSelected = undefined;
    preferences.travelEmailSelected = undefined;
    preferences.travelLetterSelected = undefined;
    preferences.travelPhoneSelected = undefined;
    preferences.hasFinancialSelected = undefined;
    preferences.financialEmailSelected = undefined;
    preferences.financialLetterSelected = undefined;
    preferences.financialPhoneSelected = undefined;
    preferences.isOptedInToAllAreas = undefined;
    preferences.hasMagazineSelected = undefined;
    preferences.magazineEmailSelected = undefined;
    preferences.magazineLetterSelected = undefined;
    preferences.magazinePhoneSelected = undefined;
};

const trackCommunicationPreferences = (preferencesVm, division, channel) => {
    publish(ADOBE_TOPICS.COMMUNICATION_PREFERENCES, {
        preferences: preferencesVm,
        reGdprData: { division: division, channel: channel }
    });
};

// * Construct request and call GDPR to get preferences. If the request is
//  * successful, stores the data (both req and response) in a sessionstorage

function callGdprService(model, callback) {
    const reqData = buildReqData(model);
    const isRetrievalInitCall = getSessionStorageItem('IS_RETRIEVAL_JOURNEY') && !getSessionStorageItem('GDPR_DATA');
    return SgCustomerPreferenceService.getCustomerPreferenceDetails(reqData)
        .then((data) => {
            const gdprData = data;
            if (gdprData) {
                saveDataInSessionKey(reqData, true);
                saveGdprResponseInSessionKey(gdprData, false);
                // set the cpcCalledForThisCustomer flag
                setSessionStorageItem('GDPR_CPCCALLED', true);
                setSessionStorageItem('GDPR_COMMSPREF', isRetrievalInitCall ? false : (gdprData.showGDPR && gdprData.showFullGdpr)); // Full GDPR Questions
                setSessionStorageItem('GDPR_RECONSENT', gdprData.showGDPR && !gdprData.showFullGdpr); // ReConsent CRF267
                if (getSessionStorageItem('GDPR_RECONSENT')) {
                    setSessionStorageItem('GDPR_RECONSENT', insuranceNotSelectedForEmailOrPost(gdprData.gdprDivision));
                    gdprData.showGDPR = getSessionStorageItem('GDPR_RECONSENT');
                    gdprData.hasSeenCommunicationPreferences = getSessionStorageItem('GDPR_RECONSENT');
                    saveGdprResponseInSessionKey(gdprData, false);
                }
                cpcPermissionId = gdprData.permissionId === null ? undefined : gdprData.permissionId;
                cpcQuestionId = gdprData.questionId;
            } else {
                setSessionStorageItem('GDPR_CPCCALLED', false);
                setSessionStorageItem('GDPR_COMMSPREF', false);
                deleteGdprData();
            }
            if (callback) {
                callback();
            }
        }).catch(() => {
            setSessionStorageItem('GDPR_CPCCALLED', false);
            setSessionStorageItem('GDPR_COMMSPREF', false); // don't show questions if the call fails
            deleteGdprData();
        });
}

//  * Init the $scope communication preference with the information stored in the sesisionstorage
//  * @param showGdpr, if defined, gdprCookie.showGDPR is ignored

const initCommPreferences = (model, showGdpr) => {
    resetGdprQuestions(model);
    const gdprSessionData = getGdprSessionObject();
    if (gdprSessionData) {
        setSessionStorageItem('GDPR_CPCCALLED', gdprSessionData.cpcCalledForThisCustomer);
        setSessionStorageItem('GDPR_COMMSPREF', showGdpr === undefined ? gdprSessionData.showGDPR : showGdpr);
        setSessionStorageItem('GDPR_RECONSENT', !gdprSessionData.showFullGdpr && gdprSessionData.showGDPR);
        if (getSessionStorageItem('GDPR_RECONSENT') || getSessionStorageItem('GDPR_COMMSPREF')) {
            SgSubmissionUtil.getAccountHolder(model).value.communicationPreferences = {
                hasEmailSelected: gdprSessionData.hasEmailSelected,
                hasLetterSelected: gdprSessionData.hasLetterSelected,
                hasPhoneSelected: gdprSessionData.hasPhoneSelected,
                hasInsuranceSelected: gdprSessionData.hasInsuranceSelected,
                insuranceEmailSelected: gdprSessionData.insuranceEmailSelected,
                insuranceLetterSelected: gdprSessionData.insuranceLetterSelected,
                insurancePhoneSelected: gdprSessionData.insurancePhoneSelected,
                hasTravelSelected: gdprSessionData.hasTravelSelected,
                travelEmailSelected: gdprSessionData.travelEmailSelected,
                travelLetterSelected: gdprSessionData.travelLetterSelected,
                travelPhoneSelected: gdprSessionData.travelPhoneSelected,
                hasFinancialSelected: gdprSessionData.hasFinancialSelected,
                financialEmailSelected: gdprSessionData.financialEmailSelected,
                financialLetterSelected: gdprSessionData.financialLetterSelected,
                financialPhoneSelected: gdprSessionData.financialPhoneSelected,
                hasMagazineSelected: gdprSessionData.hasMagazineSelected,
                magazineEmailSelected: gdprSessionData.magazineEmailSelected,
                magazineLetterSelected: gdprSessionData.magazineLetterSelected,
                magazinePhoneSelected: gdprSessionData.magazinePhoneSelected,
                isOptedInToAllAreas: gdprSessionData.isOptedInToAllAreas,
                isOptedOutToAllAreas: gdprSessionData.isOptedOutToAllAreas,
                hasSeenCommunicationPreferences: true,
                isGdprReConsent: SgGdprUtilService.getSessionStorageItem('GDPR_RECONSENT')
            };
        } else {
            SgSubmissionUtil.getAccountHolder(model).value.communicationPreferences = {
                hasSeenCommunicationPreferences: false
            };
        }
    } else {
        setSessionStorageItem('GDPR_COMMSPREF', true);
    }
};

// * Restore comm preferences from the session storage, if the storage matches with the account on the current
// * submission. Otherwise, call callGdprService first - which will call the server and store the
// * info in a new storage - and then restores the preferences.
// * Returns a promise, which is resolved once the procedure is completed.

const restoreCommPreferences = (model, retrievalFlowVisited) => {
    if (compareDataWithSessionStorage(model)) {
        initCommPreferences(model, retrievalFlowVisited ? false : undefined);
    } else if (!SgSubmissionUtil.isAggregatorQuote(model)) {
        resetGdprQuestions(model);
        callGdprService(model, () => {
            initCommPreferences(model);
        });
    }
};

const getProductPhoneNumber = () => {
    let phoneNumber;
    if (SgProductService.isCar() || SgProductService.isMotorHome()) {
        phoneNumber = CommonConstants.SAGA_PHONE_NUMBERS.SAGA_HELP_DESK_MOTOR_PHONE_NUMBER1;
    } else if (SgProductService.isTravel()) {
        phoneNumber = CommonConstants.COMM_PREFERENCE_ERROR_MODAL.TRAVEL_PHONE;
    } else {
        phoneNumber = CommonConstants.SAGA_PHONE_NUMBERS.SAGA_ERROR_HOME_PHONE_NUMBER;
    }
    return phoneNumber;
};

const getModalJsx = () => {
    return (
        <>
            {CommonConstants.COMM_PREFERENCE_ERROR_MODAL.MESSAGE}
            <a href={`tel:${getProductPhoneNumber().replaceAll(' ', '')}`}>
                &nbsp;
                {getProductPhoneNumber()}
            </a>
        </>
    );
};

const modalProps = {
    title: CommonConstants.COMM_PREFERENCE_ERROR_MODAL.TITLE,
    modalJsx: getModalJsx(),
    closeBtnLabel: CommonConstants.COMM_PREFERENCE_ERROR_MODAL.OK,
    titleVisibility: true
};

// Saves the customer preferences. If the saving is successful, updates the session storage with the response and shows error modal
async function submitGdprData(model) {
    return new Promise((resolve) => {
        if (!compareDataWithSessionStorage(model) || !compareGdprAnswersWithSessionStorage(model)) {
            SgCustomerPreferenceService.saveCustomerPreferenceDetails(buildDataToSave(model)).then((response) => {
                setSessionStorageItem('SUBMIT_GDPR_DATA', true);
                // continue if its success
                if (response.dataSavedSuccessfully === true) {
                    saveGdprAnswersInSessionStorage(model);
                    resolve();
                } else {
                    ModalNextProvider.showModal(
                        <ModalCtrl {...modalProps} />
                    ).then(() => {
                        resolve();
                    });
                }
            }).catch(() => {
                ModalNextProvider.showModal(
                    <ModalCtrl {...modalProps} />
                ).then(() => {
                    resolve();
                });
            });
        } else {
            resolve();
        }
    });
}

// ** Checks if all required Gdpr request fields are complete  */
const isGdprFieldsComplete = (modelVM) => {
    const address = getAddressVal(modelVM);
    const policyHolder = getPolicyHolderVal(modelVM);

    if (isValid(policyHolder.dateOfBirth)
        && isValid(policyHolder.dateOfBirth.day)
        && isValid(policyHolder.dateOfBirth.monthViewValue)
        && isValid(policyHolder.dateOfBirth.year)) {
        return isValid(policyHolder.firstName)
            && isValid(policyHolder.lastName)
            && (isValid(address.addressLine1) || isValid(address.addressLine2))
            && isValid(address.postalCode);
    }
    deleteGdprData();
    setSessionStorageItem('GDPR_CPCCALLED', false);
    return false;
};

const getGdprData = (modelVM, updateData, isAggsQuoteCheck) => {
    return new Promise((resolve) => {
        const cpcCalledForTheCustomer = getSessionStorageItem('GDPR_CPCCALLED');
        // check if it's an aggregator quote
        if (SgSubmissionUtil.isAggregatorQuote(modelVM)) {
            if (SgSubmissionUtil.getAccountHolder(modelVM).communicationPreferences.value === undefined) {
                SgSubmissionUtil.getAccountHolder(modelVM).communicationPreferences.value = {};
            }
            // check if the function is being used on quote page, the preferences weren't already saved, all the required data is present,
            // if the GDPR service was already called or details have been changed since the previous call
            if (isAggsQuoteCheck
                && isGdprFieldsComplete(modelVM) && ((!_.isEmpty(cpcCalledForTheCustomer) && !cpcCalledForTheCustomer)
                    || !compareDataWithSessionStorage(modelVM))) {
                // call the GDPR service as it's either new aggregator session or details have been changed
                setSessionStorageItem('GDPR_CPCCALLED', false);
                SgSessionStorageService.removeItem('GDPR_AGGS_COMPLETED');
                callGdprService(modelVM, () => {
                    events.emit(CommonConstants.GDPR_SERVICE_CALLED_EVENT);
                }).finally(() => resolve());
            } else {
                resolve();
            }
            // check if all the required data is complete
        } else if (isGdprFieldsComplete(modelVM)) {
            // Check the customer hasn't called GDPR, or gone back and changed their details.
            if ((!_.isEmpty(cpcCalledForTheCustomer) && !cpcCalledForTheCustomer) || !compareDataWithSessionStorage(modelVM)) {
                // set the cpcCalledForThisCustomer flag as its a new customer
                setSessionStorageItem('GDPR_CPCCALLED', false);
                if (updateData && SgSubmissionUtil.getAccountHolder(modelVM).communicationPreferences.value === undefined) {
                    const newModelVM = _.clone(modelVM);
                    SgSubmissionUtil.getAccountHolder(newModelVM).communicationPreferences.value = {};
                    updateData(newModelVM);
                }
                callGdprService(modelVM, () => {
                    events.emit(CommonConstants.GDPR_SERVICE_CALLED_EVENT);
                }).finally(() => resolve());
            } else {
                resolve();
            }
        } else {
            resolve();
        }
    });
};

const saveAggsGdprData = (modelVM) => {
    const gdprSessionData = SgGdprUtilService.getGdprSessionObject();
    // Re-inflate from sessionData
    SgSubmissionUtil.getAccountHolder(modelVM).value.communicationPreferences = {
        hasEmailSelected: gdprSessionData.hasEmailSelected,
        hasLetterSelected: gdprSessionData.hasLetterSelected,
        hasPhoneSelected: gdprSessionData.hasPhoneSelected,
        hasInsuranceSelected: gdprSessionData.hasInsuranceSelected,
        hasTravelSelected: gdprSessionData.hasTravelSelected,
        hasFinancialSelected: gdprSessionData.hasFinancialSelected,
        isOptedInToAllAreas: gdprSessionData.isOptedInToAllAreas,
        hasSeenCommunicationPreferences: true,
        hasMagazineSelected: gdprSessionData.hasMagazineSelected,
        insuranceEmailSelected: gdprSessionData.insuranceEmailSelected,
        insuranceLetterSelected: gdprSessionData.insuranceLetterSelected,
        insurancePhoneSelected: gdprSessionData.insurancePhoneSelected,
        travelEmailSelected: gdprSessionData.travelEmailSelected,
        travelLetterSelected: gdprSessionData.travelLetterSelected,
        travelPhoneSelected: gdprSessionData.travelPhoneSelected,
        financialEmailSelected: gdprSessionData.financialEmailSelected,
        financialLetterSelected: gdprSessionData.financialLetterSelected,
        financialPhoneSelected: gdprSessionData.financialPhoneSelected,
        magazineEmailSelected: gdprSessionData.magazineEmailSelected,
        magazineLetterSelected: gdprSessionData.magazineLetterSelected,
        magazinePhoneSelected: gdprSessionData.magazinePhoneSelected,
        isGdprReConsent: gdprSessionData.isGdprReConsent,
        scenariotwo: gdprSessionData.scenariotwo,
        showSingleDivisionMultipleChannels: gdprSessionData.showSingleDivisionMultipleChannels,
    }; // init

    return SgCustomerPreferenceService.saveCustomerPreferenceDetails(SgGdprUtilService.buildDataToSave(modelVM)).then((response) => {
        return response.dataSavedSuccessfully === true;
    }).catch(() => {
        return false;
    });
};

const shouldCallGdprService = (modelVM) => {
    const gdprSessionData = SgGdprUtilService.getGdprSessionObject();
    return gdprSessionData && SgSubmissionUtil.isAggregatorQuote(modelVM)
        && (getSessionStorageItem('GDPR_RECONSENT') || getSessionStorageItem('GDPR_COMMSPREF'));
};

const saveGdprData = async (modelVM) => {
    const preferences = SgSubmissionUtil.getAccountHolder(modelVM).communicationPreferences.value;
    const showCommunicationRePermissioning = getSessionStorageItem('GDPR_RECONSENT') || false;
    const showCommPreferences = getSessionStorageItem('GDPR_COMMSPREF') || false;
    const hasSubmittedGdprData = getSessionStorageItem('SUBMIT_GDPR_DATA');
    if (!SgSubmissionUtil.isAggregatorQuote(modelVM) && !hasSubmittedGdprData && (showCommPreferences || showCommunicationRePermissioning)) {
        if (preferences !== undefined) {
            await submitGdprData(modelVM);
        }
    }
};

const buildReconsentDivisionData = (preferences, divisions) => {
    const personalFinance = 'personalfinance';
    const holidays = 'holidays';
    const sagaMagazine = 'sagamagazine';
    if (preferences.insuranceEmailSelected) {
        if (!preferences.financialLetterSelected) {
            divisions.push(personalFinance);
        }
        if (!preferences.travelLetterSelected) {
            divisions.push(holidays);
        }
        if (!preferences.magazineLetterSelected) {
            divisions.push(sagaMagazine);
        }
    } else {
        if (!preferences.financialEmailSelected) {
            divisions.push(personalFinance);
        }
        if (!preferences.travelEmailSelected) {
            divisions.push(holidays);
        }
        if (!preferences.magazineEmailSelected) {
            divisions.push(sagaMagazine);
        }
    }
};

const trackGdprReconsentVariant = (preferences) => {
    const reconsentDetails = {};
    const method = preferences.insuranceEmailSelected ? ['letter'] : ['email'];
    const divisions = ['Insurance'];

    if (preferences.showMultipleChannels) {
        reconsentDetails.version = '01';
        reconsentDetails.method = ['all'];
        reconsentDetails.division = ['all'];
    }
    if (preferences.scenariotwo) {
        buildReconsentDivisionData(preferences, divisions);
        reconsentDetails.version = '02';
        reconsentDetails.method = method;
        reconsentDetails.division = divisions;
    }
    if (preferences.showSingleChannelSingleDivision) {
        reconsentDetails.version = '03';
        reconsentDetails.method = method;
        reconsentDetails.division = divisions;
    }
    if (preferences.showSingleDivisionMultipleChannels) {
        reconsentDetails.version = '04';
        reconsentDetails.method = ['email', 'letter'];
        reconsentDetails.division = divisions;
    }

    publish(ADOBE_TOPICS.ADOBE_EVENT, { adobeID: COMMON_TAGGING_PROPERTIES.gdprReconsentVariant, value: reconsentDetails });
};

export default {

    buildDataToSave,
    resetGdprQuestions,
    compareDataWithSessionStorage,
    getGdprSessionObject,
    saveGdprAnswersInSessionStorage,
    compareGdprAnswersWithSessionStorage,
    callGdprService,
    deleteGdprData,
    restoreCommPreferences,
    initCommPreferences,
    submitGdprData,
    setSessionStorageItem,
    getSessionStorageItem,
    isGdprFieldsComplete,
    getGdprData,
    saveAggsGdprData,
    saveGdprData,
    shouldCallGdprService,
    trackCommunicationPreferences,
    trackGdprReconsentVariant
};
